import { api } from '@goaltree/backend/convex/_generated/api'
import { useAction } from 'convex/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachIdentity } from '@/hooks/use-coach-name'

// One-at-a-time persona intro previews (the quota-exempt cached clips the coach
// persona picker plays). Clips are prefetched into ready Audio elements so a
// hover or tap starts instantly; tapping the playing voice again stops it.
export function useIntroPreview(style?: string) {
  const getIntro = useAction(api.voice.getIntroAudio)
  const { i18n } = useTranslation()
  const locale = i18n.language
  // The intro says the coach's name, so a rename must miss the old clips.
  const { customName } = useCoachIdentity()
  const cacheRef = useRef(new Map<string, Promise<HTMLAudioElement>>())
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const tokenRef = useRef(0)
  const [playing, setPlaying] = useState<string | null>(null)

  function stop() {
    tokenRef.current += 1
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    audioRef.current = null
    setPlaying(null)
  }

  useEffect(() => stop, [])

  function load(voice: string, s = style) {
    const key = `${voice}:${s ?? ''}:${locale}:${customName ?? ''}`
    let p = cacheRef.current.get(key)
    if (!p) {
      p = getIntro({ voice, style: s, locale }).then((clip) => {
        const el = new Audio(`data:${clip.contentType};base64,${clip.audio}`)
        el.preload = 'auto'
        el.load()
        return el
      })
      p.catch(() => cacheRef.current.delete(key))
      cacheRef.current.set(key, p)
    }
    return p
  }

  function prefetch(voices: readonly string[], s = style) {
    for (const v of voices) void load(v, s).catch(() => {})
  }

  async function play(voice: string) {
    stop()
    const token = tokenRef.current
    setPlaying(voice)
    try {
      const el = await load(voice)
      if (token !== tokenRef.current) return
      el.currentTime = 0
      audioRef.current = el
      const done = () => token === tokenRef.current && setPlaying(null)
      el.onended = done
      el.onerror = done
      await el.play()
    } catch {
      // Autoplay can be refused before any user gesture; previews are best-effort.
      if (token === tokenRef.current) setPlaying(null)
    }
  }

  function toggle(voice: string) {
    if (playing === voice) stop()
    else void play(voice)
  }

  return { playing, play, toggle, stop, prefetch }
}
